import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography, CardContent, Grid, Backdrop } from "@material-ui/core";
import moment from "moment";
import "moment-timezone";
import { filterBy } from "src/common/utils/helpers";
import TabHeader from "src/components/TabHeader/TabHeader";
import { useStyles } from "./style";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { useFormik } from "formik";
import { object, string } from "yup";
import { GridRowModel } from "@material-ui/x-grid";
import CommonButton from "src/components/CommonButton/CommonButton";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { filteryByArr, StuffDestuffType } from "src/common/utils/app.dropdowns";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { useDispatch } from "react-redux";

interface Props {}

export function StuffDestuffReport({}: Props): React.ReactElement {
  const columns = [
    {
      accessorKey: "srNo",
      header: "Sr No",
      type: "string",
      size: 80,
      accessorFn: (_row: any, index: number) => index + 1,
    },
    {
      accessorKey: "indexNo",
      header: "Index No",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.indexNo || " "}`,
    },
    {
      accessorKey: "containerNumber",
      header: "Container Number",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.containerNumber || " "}`,
    },
    {
      accessorKey: "virNo",
      header: "VIR Number",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.virNo || " "}`,
    },
    {
      accessorKey: "description",
      header: "Description",
      type: "string",
      size: 250,
      accessorFn: (row: any) => ` ${row.description || " "}`,
    },
    {
      accessorKey: "markAndNumbers",
      header: "Mark & Numbers",
      type: "string",
      size: 250,
      accessorFn: (row: any) => ` ${row.markAndNumbers || " "}`,
    },
    {
      accessorKey: "destuffDate",
      header: "Destuff Date",
      type: "string",
      size: 250,
      accessorFn: (row: any) => ` ${row.destuffDate || " "}`,
    },
    {
      accessorKey: "consignee",
      header: "Consignee",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.consignee || " "}`,
    },
    {
      accessorKey: "vessel",
      header: "Vessel",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.vessel || " "}`,
    },
    {
      accessorKey: "arrivalDate",
      header: "Arrival Date",
      type: "string",
      size: 200,
      accessorFn: function customCell(row: any) {
        return `${row.arrivalDate ? moment(row.arrivalDate).format("DD-MM-YYYY") : ""}`;
      },
    },
    {
      accessorKey: "sealNumber",
      header: "Seal Number",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.sealNumber || " "}`,
    },
  ];
  const classes = useStyles();

  const moduleName = "Stuff/Destuff Report";
  const pageHeading = "Stuff/Destuff Report";
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [gridRecord, setGridRecord] = React.useState<GridRowModel[]>([]);
  const [loader, setLoader] = useState<any>(false);
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const [isCustomDate, setIsCustomDate] = useState<boolean>(false);
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);
  const dispatch = useDispatch();

  const validationSchema = object({
    startDate: string().required("This field is required."),
    endDate: string().required("This field is required."),
  });

  const formik = useFormik({
    initialValues: {
      filterBy: "today",
      startDate: moment().startOf("day").format("YYYY-MM-DDTHH:mm"),
      endDate: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
      reportType: "Stuff",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const body = {
        startDate: values.startDate || null,
        endDate: values.endDate || null,
        reportType: values.reportType || null,
      };
      setGridRecord([]);
      setLoader(true);
      try {
        const results = await fetchWrapper.post("get-stuff-and-destuff-report", body);
        setGridRecord(results?.value?.report || []);
        setDisplayColumns(columns);
      } catch (error: any) {
        setLoader(false);
        dispatch(
          addNotificationAction({
            message: error.message || "Error fetching report data",
            options: { variant: "error" },
          })
        );
      }
      setLoader(false);
    },
  });

  const exportHandler = async (rows: any[]) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );

    const exportedRecords = rows.map((data: any) => {
      return {
        "Index No": data?.indexNo || "",
        "Container Number": data?.containerNumber || "",
        "VIR Number": data?.virNo || "",
        Description: data?.description || "",
        Consignee: data?.consignee || "",
        Vessel: data?.vessel || "",
        "Arrival Date": data?.arrivalDate ? moment(data?.arrivalDate).format("DD-MM-YYYY") : "",
        "Seal Number": data?.sealNumber || "",
      };
    });

    return exportedRecords;
  };

  const filterByAction = (data: any) => {
    formik.setFieldValue("filterBy", data.value);
    if (data && data.value) {
      const res = filterBy(data);
      if (res?.isCustomDate) {
        setIsCustomDate(true);
        formik.setFieldValue("startDateCustom", res?.startDate);
        formik.setFieldValue("endDateCustom", res?.endDate);
      } else {
        setIsCustomDate(false);
      }
      formik.setFieldValue("startDate", res?.startDate);
      formik.setFieldValue("endDate", res?.endDate);
    }
  };

  const resetSearch = () => {
    formik.resetForm();
    setGridRecord([]);
    formik.setFieldValue("filterBy", "today");
    formik.setFieldValue("startDate", moment().startOf("day").format("YYYY-MM-DDTHH:mm"));
    formik.setFieldValue("endDate", moment().endOf("day").format("YYYY-MM-DDTHH:mm"));
    formik.setFieldValue("reportType", "Stuff");
  };

  const fetchData = async () => {
    const body = {
      startDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(0, 0)).format(
        "YYYY-MM-DDTHH:mm"
      ),
      endDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(23, 59)).format(
        "YYYY-MM-DDTHH:mm"
      ),
      reportType: formik.values.reportType || "Stuff",
    };
    setGridRecord([]);
    setLoader(true);
    try {
      const results = await fetchWrapper.post("get-stuff-and-destuff-report", body);
      setGridRecord(results?.value?.report || []);
      setDisplayColumns(columns);
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message || "Error fetching report data",
          options: { variant: "error" },
        })
      );
    }
    setLoader(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => formik.submitForm()}
                >
                  Search
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <>
            <Grid spacing={1} container direction="row">
              <Grid item xs={12} md={3} lg={2}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  error={formik.touched.filterBy && Boolean(formik.errors.filterBy)}
                  helperText={formik.touched.filterBy && formik.errors.filterBy}
                  label="Filter by"
                  placeholder="Filter by"
                  name="filterBy"
                  disableClearable={true}
                  optionsType={filteryByArr}
                  getOptionLabel={(option: any) => option.label || null}
                  onChange={(row: any) => {
                    row ? formik.setValues({ ...formik.values, filterBy: row.value || "" }) : "";
                    if (row === null) {
                      formik.setFieldValue("filterBy", null);
                    }
                    filterByAction(row);
                  }}
                  value={
                    filteryByArr.find(({ value }: any) => value === formik.values.filterBy) || ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Report Type"
                  name="reportType"
                  placeholder="Select Type"
                  disableClearable={true}
                  optionsType={StuffDestuffType}
                  getOptionLabel={(option: any) => option || ""}
                  onChange={(row: any) => {
                    row ? formik.setFieldValue("reportType", row || "") : "";
                    if (row === null) {
                      formik.setFieldValue("reportType", "Stuff");
                    }
                  }}
                  value={
                    StuffDestuffType.find((value: any) => value === formik.values.reportType) || ""
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                lg={2}
                className={!isCustomDate ? classes.hideDatesGrid : ""}
              >
                <CommonTextFieldDate
                  cssClassName="customTextField"
                  label={"Start Date"}
                  name={"startDate"}
                  datePickerRef={startDatePickerRef}
                  value={formik.values.startDate}
                  onChange={(e: any) => {
                    formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                    formik.setFieldValue("endDate", "");
                  }}
                  maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                lg={2}
                className={!isCustomDate ? classes.hideDatesGrid : ""}
              >
                <CommonTextFieldDate
                  cssClassName="customTextField"
                  label={"End Date"}
                  name={"endDate"}
                  datePickerRef={endDatePickerRef}
                  value={formik.values.endDate}
                  onChange={(e: any) => {
                    formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  }}
                  minDate={formik.values.startDate}
                  maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                />
              </Grid>
            </Grid>
          </>
          <CommonMaterialGrid
            rows={gridRecord}
            columns={displayColumns}
            defaultPerPageRecord={15}
            loader={loader}
            height={550}
            isExport={isExport}
            exportHandler={exportHandler}
            filename={`Stuff_Destuff_report_${Date.now()}`}
            isFooter={true}
          />
        </CardContent>
      </Card>
    </>
  );
}
